<template>
  <v-container fluid>
    <page-title icon="fas fa-fw fa-copy">
      Public Files
      <template v-slot:buttons>

      </template>
    </page-title>

    <v-card class="mb-4">
      <v-card-title>
        <v-btn color="primary"
               class="mr-2"
               :disabled="busy"
               @click.prevent="showUploadFilesDialog = true">
          Upload Files
        </v-btn>
        <v-btn class="mr-2"
               :disabled="busy"
               @click.prevent="showCreateFolderDialog = true">
          Create Folder
        </v-btn>
        <v-btn @click.prevent="fetchData"
               :disabled="busy"
               class="mr-2">
          Refresh
        </v-btn>
        <v-btn color="error"
               @click.prevent="deleteAssets"
               :disabled="busy || selected.length === 0"
               class="mr-2">
          Delete
        </v-btn>
      </v-card-title>
      <v-data-table id="assets-table"
                    v-model="selected"
                    show-select
                    :single-select="false"
                    :items="assets"
                    item-key="uuid"
                    :headers="headers"
                    :items-per-page="100"
                    :footer-props="{'items-per-page-options': [50, 100, -1]}"
                    :hide-default-footer="busy">

        <template v-if="busy"
                  v-slot:body="{items}">
          <tr>
            <td colspan="4">
              <div class="text-center py-4">
                <v-progress-circular size="42"
                                     indeterminate>
                </v-progress-circular>
              </div>
            </td>
          </tr>
        </template>


        <template v-if="up"
                  v-slot:body.prepend="{items: assets}">
          <tr>
            <td>

            </td>
            <td>
              <router-link :to="{name: 'admin.team_assets.index', query: {path: up}}"
                           class="text-decoration-none"
                           title="Go up one directory">
                <v-icon left>
                  fas fa-fw fa-level-up fa-flip-horizontal
                </v-icon>
                Up
              </router-link>
            </td>
            <td>

            </td>
            <td>

            </td>
          </tr>
        </template>

        <template v-slot:item.basename="{item: asset}">
          <div v-if="asset.is_file"
               class="d-flex align-center">
            <v-icon left>
              fal fa-fw {{ asset.icon_v2 }}
            </v-icon>
            <div class="mr-1">
              {{ asset.basename }}
            </div>
            <v-btn icon
                   small
                   @click.prevent="copyToClipboard(asset)"
                   title="Copy URL to clipboard">
              <v-icon small>
                fas fa-fw fa-copy
              </v-icon>
            </v-btn>
            <v-btn icon
                   small
                   :href="asset.url"
                   target="_blank"
                   title="Open URL in a new tab">
              <v-icon small>
                fas fa-fw fa-external-link-alt
              </v-icon>
            </v-btn>
          </div>
          <div v-else>
            <router-link :to="{name: 'admin.team_assets.index', query: {path: asset.relative_path}}"
                         class="text-decoration-none"
                         title="Open URL">
              <v-icon left>
                fas fa-fw fa-folder
              </v-icon>
              {{ asset.basename }}
            </router-link>
          </div>
        </template>

        <template v-slot:item.last_modified_at="{item: asset}">
          <template v-if="asset.is_file">
            {{ asset.last_modified_at | datetime }}
          </template>
        </template>

        <template v-slot:item.size="{item: asset}">
          <template v-if="asset.is_file">
            {{ asset.size | number }}
          </template>
        </template>
      </v-data-table>
    </v-card>

    <create-folder-dialog v-if="showCreateFolderDialog"
                          :dialog="showCreateFolderDialog"
                          :path="path"
                          @created="fetchData(path)"
                          @canceled="showCreateFolderDialog = false">
    </create-folder-dialog>

    <upload-files-dialog v-if="showUploadFilesDialog"
                         :dialog="showUploadFilesDialog"
                         :path="path"
                         @uploaded="fetchData(path)"
                         @canceled="showUploadFilesDialog = false">
    </upload-files-dialog>
  </v-container>
</template>
<script>
import PageTitle from '../../components/PageTitle.vue';
import path from 'path-browserify';
import {isUndefined, orderBy, trim} from 'lodash';
import {Form} from 'vform';
import pluralize from 'pluralize';
import teamAssetService from '../../../../ml/services/teamAssetService';
import clipboardService from '../../../../ml/services/clipboardService';
import CreateFolderDialog from './components/CreateFolderDialog.vue';
import UploadFilesDialog from './components/UploadFilesDialog.vue';

export default {
  components: {UploadFilesDialog, CreateFolderDialog, PageTitle},
  data() {
    return {
      assets: [],
      busy: false,
      form: new Form({}),
      selected: [],
      showCreateFolderDialog: false,
      showUploadFilesDialog: false,
    }
  },
  computed: {
    path() {
      if (isUndefined(this.$route.query.path) || this.$route.query.path === null) {
        return '/';
      }
      let p = trim(this.$route.query.path);
      if (p === '') {
        return '/';
      }
      return path.normalize(p);
    },
    up() {
      if (this.path && this.path !== '/') {
        return path.dirname(this.path);
      }
      return null;
    },
    headers() {
      return [
        {
          text: 'Name',
          value: 'basename',
          sortable: false,
        },
        {
          text: 'Last Modified',
          value: 'last_modified_at',
        },
        {
          text: 'Size',
          value: 'size',
        },
      ]
    },
    selectedFiles() {
      return this.selected.filter(a => a.is_file);
    },
    selectedFolders() {
      return this.selected.filter(a => a.is_dir);
    },
  },
  beforeRouteUpdate(to, from, next) {
    if (to.query.path) {
      this.resetSelected();
      this.fetchData(to.query.path);
    }
    next();
  },
  created() {
    this.fetchData(this.path);
  },
  methods: {
    closeDialogs() {
      this.showCreateFolderDialog = false;
      this.showUploadFilesDialog = false;
    },
    fetchData(path) {
      this.closeDialogs();
      if (!path) {
        path = this.path;
      }
      this.busy = true;
      return teamAssetService.getAssets(path)
          .then(data => {
            this.assets = orderBy(data, ['is_dir', 'slug'], ['desc', 'asc']);
          })
          .finally(() => {
            this.busy = false;
          })
    },
    deleteAssets() {
      if (this.form.busy || this.busy) {
        return;
      }
      if (this.selected.length === 0) {
        return;
      }
      if (confirm('Are you sure you want to delete the selected folders and/or files?')) {
        this.busy = true;
        const events = [];
        if (this.selectedFolders.length > 0) {
          events.push(...this.selectedFolders.map(asset => teamAssetService.deleteDirectory(asset.relative_path)));
        }
        if (this.selectedFiles.length > 0) {
          events.push(teamAssetService.deleteFiles(this.selectedFiles.map(f => f.relative_path)));
        }
        if (events.length === 0) {
          return;
        }
        return Promise.all(events)
            .then(data => {
              setTimeout(() => {
                this.$toast.success(`${pluralize('item', this.selected.length, true)} deleted.`);
                this.selected = [];
                this.fetchData(this.path);
              }, 1000);
            })
            .catch(err => {
              this.$toast.error('There was an error.');
              this.busy = false;
            })
      }
    },
    copyToClipboard(asset) {
      clipboardService.copy(asset.url)
          .then(() => {
            this.$toast.success('URL copied to clipboard.');
          })
    },
    resetSelected() {
      this.selected.length = 0;
    }
  }
}
</script>
